import React, { Component } from "react";
import {
  loading,
  alert,
  NavbarAdmin,
  Select,
  format_date,
  float,
  GET,
  PUT,
  t,
  can,
} from "../../components/CustomComponent.js";
import { bankLogo, PROMPTPAY_LOGO } from "../../components/topup_option.js";
import {
  DEFAULT_WALLET_SETTING,
  PREVIEW_AMOUNTS,
  MAX_RATE,
  MAX_COIN_VALUE,
  clone,
  isUnlimited,
  coinFromBaht,
  fmtCoin,
} from "../../components/wallet_option.js";
import "../../assets/css/property.css";
import "../../assets/css/people.css";
import "../../assets/css/settings.css";

/* ============================================================================
 * ตั้งค่า › ตั้งค่าเหรียญ  (/ad/wallet-settings)
 *
 * นโยบายของระบบเหรียญที่กระทบทุกกระเป๋าพร้อมกัน : เปิด/ปิดระบบ ชื่อที่ใช้เรียกเหรียญ
 * อัตราแลกจากเงินบาท เหรียญต้อนรับตอนเปิดกระเป๋า และเพดานของการเติม/ปรับยอด
 * คนละเรื่องกับการปรับยอดของพาร์ตเนอร์รายคน ซึ่งทำจากเมนูพาร์ตเนอร์
 *
 * ต่อกับ API แล้ว :
 *   GET  admin/wallets/settings   (Get)
 *   PUT  admin/wallets/settings   (Save)
 * ========================================================================== */

const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";
const locale = () => (isTh() ? "th" : "en");

/* ช่องตัวเลขที่ถูกลบจนว่างให้กลับไปใช้ค่าตั้งต้น — services/api ตัดค่าว่างทิ้งก่อนส่ง
   ถ้าปล่อยว่างไว้ ฟิลด์นั้นจะไม่ถูกส่งเลยแล้วค้างค่าเดิมในฐานข้อมูลโดยที่หน้าจอโชว์ว่าง */
const numeric = (value, key) => (String(value ?? "").trim() === "" ? DEFAULT_WALLET_SETTING[key] : String(value));

/* ช่องข้อความที่ลบจนว่างแล้ว "ตั้งใจให้ว่าง" — ค่าว่างถูก services/api ตัดทิ้งก่อนส่ง
   ถ้าไม่แปลงเป็น "-" ฟิลด์นั้นจะไม่ถูกส่งเลย แล้วค่าเดิมในฐานข้อมูลจะค้างอยู่
   ทั้งที่หน้าจอโชว์ว่าง (กลไกเดียวกับหน้าลายน้ำ) */
const clearable = (value) => (String(value || "").trim() ? String(value).trim() : "-");

export default class WalletSetting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: clone(DEFAULT_WALLET_SETTING),  // ค่าที่กำลังแก้
      saved: clone(DEFAULT_WALLET_SETTING), // ค่าที่บันทึกไว้ล่าสุด (ไว้เทียบว่ามีแก้ค้างไหม)
      updated_at: null,
      updated_by: "",
    };
  }

  componentDidMount() {
    loading(false);
    this.Get();
  }

  /* ------------------------------------------------------------ ดึงค่าตั้งค่า */
  Get = async () => {
    loading(true);
    const result = await GET("admin/wallets/settings");
    loading(false);

    if (!result?.status) return;

    this.applyResult(result.data);
  };

  // ใช้ค่าที่ backend ตอบกลับเป็นค่าล่าสุดเสมอ (ตัวเลขถูกจัดรูปมาแล้วจากฝั่ง API)
  applyResult = (data) => {
    const setting = { ...DEFAULT_WALLET_SETTING, ...(data || {}) };

    this.setState({
      data: clone(setting),
      saved: clone(setting),
      updated_at: data?.updated_at || null,
      updated_by: data?.updated_by || "",
    });
  };

  set = (key, value) => this.setState((prev) => ({ data: { ...prev.data, [key]: value } }));

  isDirty = () => JSON.stringify(this.state.data) !== JSON.stringify(this.state.saved);

  /* ------------------------------------------------------------ บันทึก/คืนค่า */
  Save = async () => {
    const { data } = this.state;

    if (!String(data.coin_name || "").trim())
      return alert("", "warning", t("warning"), t("verify_coin_name"));

    // อัตรา 0 = เติมเงินเท่าไรก็ได้เหรียญ 0 ซึ่งไม่ใช่ค่าที่ตั้งใจตั้ง (backend ก็ปฏิเสธ)
    if (!(Number(data.coin_per_baht) > 0))
      return alert("", "warning", t("warning"), t("verify_coin_rate"));

    // เพดาน 0 แปลว่าไม่จำกัด จึงเทียบเฉพาะตอนที่ตั้งเพดานไว้จริง
    if (Number(data.max_topup) > 0 && Number(data.min_topup) > Number(data.max_topup))
      return alert("", "warning", t("warning"), t("verify_topup_range"));

    if (!(await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel")))) return;

    loading(true);

    const payload = {
      enabled: data.enabled,
      coin_name: String(data.coin_name).trim(),
      coin_per_baht: numeric(data.coin_per_baht, "coin_per_baht"),
      welcome_bonus: numeric(data.welcome_bonus, "welcome_bonus"),
      min_topup: numeric(data.min_topup, "min_topup"),
      max_topup: numeric(data.max_topup, "max_topup"),
      low_balance: numeric(data.low_balance, "low_balance"),
      max_adjust: numeric(data.max_adjust, "max_adjust"),

      payment_bank: clearable(data.payment_bank),
      payment_account_no: clearable(data.payment_account_no),
      payment_account_name: clearable(data.payment_account_name),
      payment_promptpay: clearable(data.payment_promptpay),
      payment_note: clearable(data.payment_note),
    };

    const result = await PUT("admin/wallets/settings", payload);
    loading(false);

    if (!result?.status) return;

    this.applyResult(result.data);
    alert("", "success", t("success"), t("save_settings_done"));
  };

  Reset = async () => {
    if (!this.isDirty()) return;
    if (!(await alert("", "warning", t("reset_changes"), t("discard_changes_confirm"), t("confirm"), t("cancel")))) return;
    this.setState((prev) => ({ data: clone(prev.saved) }));
  };

  /* ======================================================= ตัวช่วย render */
  field = (label, control, { hint = "" } = {}) => (
    <>
      <label className="prop-label">{label}</label>
      {control}
      {!!hint && <div className="ppl-sub mt-1">{hint}</div>}
    </>
  );

  /* ช่องกรอกตัวเลขพร้อมหน่วยต่อท้าย
     รับทศนิยมได้ตามที่คอลัมน์ในฐานข้อมูลรองรับ (อัตราแลกเปลี่ยน 4 ตำแหน่ง ที่เหลือ 2)
     กันค่าเกินเพดานตั้งแต่ตอนพิมพ์ ไม่ปล่อยให้ไปตกที่ตอนกดบันทึก */
  number = (key, unit, { decimals = 2, max = MAX_COIN_VALUE } = {}) => (
    <div className="set-affix set-affix--wide">
      <input
        type="text"
        inputMode="decimal"
        className="prop-field"
        value={this.state.data[key]}
        onChange={(e) => {
          const value = e.target.value;
          if (value !== "" && !float(value, decimals)) return;
          if (Number(value) > max) return;
          this.set(key, value);
        }}
      />
      <span className="set-affix__unit">{unit}</span>
    </div>
  );

  text = (key, { max = 100, placeholder = "" } = {}) => (
    <input
      type="text"
      className="prop-field"
      maxLength={max}
      placeholder={placeholder}
      value={this.state.data[key]}
      onChange={(e) => this.set(key, e.target.value)}
    />
  );

  section = (icon, title, desc, children) => (
    <div className="ppl-section">
      <div className="ppl-section__title">
        <span className="icon">{icon}</span>
        {title}
      </div>
      <div className="ppl-section__desc">{desc}</div>
      {children}
    </div>
  );

  /* ข้อความกำกับใต้ช่องที่ 0 = ไม่จำกัด — ผู้ใช้จะได้ไม่เข้าใจว่าตั้งเพดานไว้ที่ศูนย์ */
  limitHint = (key, hint) => (isUnlimited(key, this.state.data[key]) ? t("no_limit_hint") : hint);

  /* ================================================ render : กล่องพรีวิว */
  renderPreview = () => {
    const { data } = this.state;
    const coin = String(data.coin_name || "").trim() || t("coin_unit");
    const rate = Number(data.coin_per_baht) || 0;

    return (
      <div className="prop-card prop-in" style={{ position: "sticky", top: "82px" }}>
        <div className="prop-card__head">
          <span className="prop-title text-14">
            <span className="prop-title__dot" />
            {t("coin_preview")}
          </span>
          <span className={`ppl-pill ${data.enabled ? "ppl-pill--on" : "ppl-pill--off"}`}>
            <span className="ppl-pill__dot" />
            {data.enabled ? t("active") : t("inactive")}
          </span>
        </div>

        <div className="p-3">
          {/* อัตราแลกเปลี่ยนตัวใหญ่ — ค่าที่คนเปิดหน้านี้มาดูบ่อยที่สุด */}
          <div className="set-rate">
            <span className="set-rate__baht">1 {t("baht")}</span>
            <span className="set-rate__eq icon">{"\uf362"}</span>
            <span className="set-rate__coin">
              {fmtCoin(rate)} <small>{coin}</small>
            </span>
          </div>

          <div className="ppl-sub mt-3">{t("coin_preview_hint")}</div>

          {/* ตัวอย่างการคำนวณ — เห็นทันทีว่าอัตราที่ตั้งไว้แปลว่าอะไรในทางปฏิบัติ */}
          <div className="set-table mt-2">
            {PREVIEW_AMOUNTS.map((amount) => (
              <div className="set-table__row" key={amount}>
                <span>{fmtCoin(amount)} {t("baht")}</span>
                <span className="icon text-12">{"\uf105"}</span>
                <b>{fmtCoin(coinFromBaht(amount, rate))} {coin}</b>
              </div>
            ))}
          </div>

          {!data.enabled && <div className="ppl-note mt-3"><span className="icon">{"\uf05a"}</span><div>{t("coin_off_hint")}</div></div>}

          {/* สรุปค่าที่ตั้งไว้ อ่านรวดเดียวจบ */}
          <div className="mt-3 d-flex flex-wrap gap-2">
            <span className="set-sample">
              {t("coin_welcome_bonus")} <b>{fmtCoin(data.welcome_bonus)}</b> {coin}
            </span>
            <span className="set-sample">
              {t("coin_min_topup")} <b>{fmtCoin(data.min_topup)}</b> {t("baht")}
            </span>
            <span className="set-sample">
              {t("coin_max_topup")}{" "}
              {isUnlimited("max_topup", data.max_topup) ? (
                <b>{t("unlimited")}</b>
              ) : (
                <>
                  <b>{fmtCoin(data.max_topup)}</b> {t("baht")}
                </>
              )}
            </span>
            <span className="set-sample">
              {t("coin_max_adjust")}{" "}
              {isUnlimited("max_adjust", data.max_adjust) ? (
                <b>{t("unlimited")}</b>
              ) : (
                <>
                  <b>{fmtCoin(data.max_adjust)}</b> {coin}
                </>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { data, updated_at, updated_by } = this.state;
    const dirty = this.isDirty();
    const coin = String(data.coin_name || "").trim() || t("coin_unit");

    /* มีปลายทางให้โอนอย่างน้อยหนึ่งช่องทางไหม — ไม่มีเลยแปลว่าพาร์ตเนอร์เติมเหรียญไม่ได้
       ต้องเตือนตรงนี้ ไม่ใช่ปล่อยให้ไปเจอตอนเปิดฟอร์มเติมแล้วไม่มีบัญชีขึ้น */
    const hasAccount =
      !!String(data.payment_account_no || "").trim() || !!String(data.payment_promptpay || "").trim();

    return (
      <div className="w-100 min-vh-100 prop-page">
        <NavbarAdmin />
        <div className="body p-4">
          {/* หัวข้อหน้า */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 prop-in">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("wallet_setting")}</h3>
              <div className="text-secondary text-14">{t("wallet_setting_page_desc")}</div>
            </div>
            <div className="text-secondary text-12 text-lg-end">
              {!!updated_at && (
                <div>
                  {t("updated")} {format_date(updated_at, "dd mmm yyyy hh:mm", locale())}
                </div>
              )}
              {!!updated_by && (
                <div>
                  {t("updated_by")} {updated_by}
                </div>
              )}
            </div>
          </div>

          <div className="row g-3">
            {/* ค่าที่ตั้งได้ */}
            <div className="col-xl-7 col-lg-7">
              <div className="prop-card prop-in">
                <div className="p-4">
                  {this.section(
                    "\uf51e",
                    t("coin_system"),
                    t("coin_system_desc"),
                    <>
                      <div className="set-row mb-3">
                        <div className="min-w-0">
                          <div className="set-row__title">{t("coin_enabled")}</div>
                          <div className="set-row__desc">{t("coin_enabled_desc")}</div>
                        </div>
                        <label className="ppl-switch">
                          <input type="checkbox" checked={data.enabled} onChange={(e) => this.set("enabled", e.target.checked)} />
                          <span className="ppl-switch__track" />
                        </label>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          {this.field(
                            t("coin_name"),
                            <input
                              type="text"
                              className="prop-field"
                              maxLength={50}
                              value={data.coin_name}
                              onChange={(e) => this.set("coin_name", e.target.value)}
                            />,
                            { hint: t("coin_name_hint") }
                          )}
                        </div>
                        <div className="col-md-6">
                          {this.field(t("coin_rate"), this.number("coin_per_baht", `${coin}/${t("baht")}`, { decimals: 4, max: MAX_RATE }), {
                            hint: t("coin_rate_hint"),
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {this.section(
                    "\uf06b",
                    t("coin_earning"),
                    t("coin_earning_desc"),
                    <div className="row g-3">
                      <div className="col-md-6">
                        {this.field(t("coin_welcome_bonus"), this.number("welcome_bonus", coin), {
                          hint: t("coin_welcome_bonus_hint"),
                        })}
                      </div>
                      <div className="col-md-6">
                        {this.field(t("coin_low_balance"), this.number("low_balance", coin), {
                          hint: this.limitHint("low_balance", t("coin_low_balance_hint")),
                        })}
                      </div>
                    </div>
                  )}

                  {this.section(
                    "\uf1de",
                    t("coin_limit"),
                    t("coin_limit_desc"),
                    <div className="row g-3">
                      <div className="col-md-6">
                        {this.field(t("coin_min_topup"), this.number("min_topup", t("baht")), {
                          hint: t("coin_min_topup_hint"),
                        })}
                      </div>
                      <div className="col-md-6">
                        {this.field(t("coin_max_topup"), this.number("max_topup", t("baht")), {
                          hint: this.limitHint("max_topup", t("coin_max_topup_hint")),
                        })}
                      </div>
                      <div className="col-md-6">
                        {this.field(t("coin_max_adjust"), this.number("max_adjust", coin), {
                          hint: this.limitHint("max_adjust", t("coin_max_adjust_hint")),
                        })}
                      </div>
                      <div className="col-12">
                        <div className="ppl-note">
                          <span className="icon">{"\uf05a"}</span>
                          <div>{t("coin_adjust_note")}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* บัญชีรับเงิน — ฟอร์มเติมเหรียญของพาร์ตเนอร์เอาไปแสดงว่าโอนเข้าบัญชีไหน
                      ช่องไหนไม่ได้กรอกก็แค่ไม่ขึ้นในฟอร์ม ไม่ได้ทำให้ระบบเหรียญพัง
                      แต่ถ้าไม่กรอกเลย พาร์ตเนอร์จะเติมเหรียญไม่ได้เพราะไม่รู้ปลายทาง */}
                  {this.section(
                    "\uf19c",
                    t("coin_payment"),
                    t("coin_payment_desc"),
                    <div className="row g-3">
                      <div className="col-md-5">
                        {this.field(
                          t("topup_bank"),
                          <Select
                            className="prop-field prop-select"
                            placeholder={t("select")}
                            value={data.payment_bank}
                            /* front = โลโก้ธนาคารจาก assets/images/bank แสดงทั้งในรายการที่กางออก
                               และหน้าช่องที่เลือกไว้ คนจำธนาคารจากสีและโลโก้ก่อนอ่านชื่อ หาเจอเร็วกว่ามาก
                               ธนาคารที่ยังไม่มีไฟล์โลโก้ให้ปล่อยว่างไปเลย ดีกว่าใส่ <img src=""> ซึ่ง
                               เบราว์เซอร์จะขึ้นเป็นรูปเสียคาช่อง */
                            options={(global.bank || []).map((item) => {
                              const logo = bankLogo(item.value);
                              return {
                                value: item.value,
                                label: isTh() ? item.name_th : item.label,
                                front: logo ? <img className="set-bank__logo" src={logo} alt="" /> : null,
                              };
                            })}
                            onChange={(value) => this.set("payment_bank", value || "")}
                          />
                        )}
                      </div>
                      <div className="col-md-3">
                        {this.field(t("topup_account_no"), this.text("payment_account_no", { max: 50, placeholder: "123-4-56789-0" }))}
                      </div>
                      <div className="col-md-4">
                        {this.field(t("topup_account_name"), this.text("payment_account_name", { max: 150 }))}
                      </div>

                      <div className="col-md-5">
                        <label className="prop-label">{t("coin_promptpay")}</label>
                        <div className="set-bank">
                          {!!PROMPTPAY_LOGO && <img className="set-bank__logo" src={PROMPTPAY_LOGO} alt="" />}
                          {this.text("payment_promptpay", { max: 50 })}
                        </div>
                        <div className="ppl-sub mt-1">{t("coin_promptpay_hint")}</div>
                      </div>
                      <div className="col-md-7">
                        {this.field(t("coin_payment_note"), this.text("payment_note", { max: 255 }), {
                          hint: t("coin_payment_note_hint"),
                        })}
                      </div>

                      {!hasAccount && (
                        <div className="col-12">
                          <div className="ppl-note ppl-note--warn">
                            <span className="icon">{"\uf06a"}</span>
                            <div>{t("coin_payment_missing")}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* แถบบันทึก — ตรึงท้ายกล่องให้กดได้ทันทีแม้เนื้อหายาว
                    ไม่มีสิทธิ์แก้ = ไม่ต้องมีแถบบันทึก ดูค่าที่ตั้งไว้ได้อย่างเดียว */}
                {can("wallet-settings.update") && (
                  <div className="ppl-savebar">
                    {dirty ? (
                      <span className="ppl-dirty">
                        <span className="ppl-dirty__dot" />
                        {t("unsaved_changes")}
                      </span>
                    ) : (
                      <span className="text-secondary text-12 d-flex align-items-center gap-2">
                        <span className="icon">{"\uf058"}</span>
                        {t("all_changes_saved")}
                      </span>
                    )}

                    <div className="d-flex gap-2">
                      <button type="button" className="prop-btn prop-btn--ghost" disabled={!dirty} onClick={this.Reset}>
                        <span className="icon text-12">{"\uf0e2"}</span>
                        {t("reset_changes")}
                      </button>
                      <button type="button" className="prop-btn" disabled={!dirty} onClick={this.Save}>
                        <span className="icon text-12">{"\uf0c7"}</span>
                        {t("save_settings")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* พรีวิว */}
            <div className="col-xl-5 col-lg-5">{this.renderPreview()}</div>
          </div>
        </div>
      </div>
    );
  }
}
